import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, Input, DateField } from '@structural-climbing/ui'
import { competitionState, type CompetitionRound, type CompetitionState } from './types'

interface Props {
  competitionId: string
  /** The competition window; a round has to sit inside it. */
  startsAt: string
  endsAt: string
  /**
   * Undefined while a deploy is mid-flight and the Worker still predates
   * rounds, so it is tolerated rather than assumed. Same guard CategoriesTab
   * carries, for the same reason.
   */
  rounds: CompetitionRound[] | undefined
  /** Gym-local wall clock, for deciding which round is live right now. */
  localNow: string
  /** The competition is over, so its shape is the result and cannot change. */
  locked: boolean
  onRefresh: () => void
}

interface Draft {
  name: string
  starts_at: string
  ends_at: string
}

const EMPTY: Draft = { name: '', starts_at: '', ends_at: '' }

/** Trims a stored wall-clock value to the minute-precision value a DateField takes. */
function bound(value: string): string {
  return value.slice(0, 16)
}

/** A round spans days, so it shows the date as well as the time. */
function stamp(value: string): string {
  return value.slice(0, 16).replace('T', ' ')
}

const STATE_STYLES: Record<CompetitionState, string> = {
  upcoming: 'bg-default/40 text-foreground/60',
  in_progress: 'bg-success/15 text-success-700',
  ended: 'bg-default/40 text-foreground/60',
}

const STATE_KEYS: Record<CompetitionState, string> = {
  upcoming: 'competitions.round_upcoming',
  in_progress: 'competitions.round_live',
  ended: 'competitions.round_ended',
}

export function RoundsTab({
  competitionId,
  startsAt,
  endsAt,
  rounds: given,
  localNow,
  locked,
  onRefresh,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const rounds = given ?? []

  const [draft, setDraft] = useState<Draft>({
    ...EMPTY,
    // Opening on the competition's own window makes the common case one tap.
    starts_at: bound(startsAt),
    ends_at: bound(endsAt),
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function resetForm() {
    setDraft({ ...EMPTY, starts_at: bound(startsAt), ends_at: bound(endsAt) })
    setEditingId(null)
    setError(null)
  }

  function startEditing(round: CompetitionRound) {
    setDraft({
      name: round.name,
      starts_at: bound(round.starts_at),
      ends_at: bound(round.ends_at),
    })
    setEditingId(round.id)
    setError(null)
  }

  async function saveRound() {
    if (!draft.name.trim() || !draft.starts_at || !draft.ends_at) {
      setError(t('competitions.errorRoundFields'))
      return
    }
    if (draft.ends_at <= draft.starts_at) {
      setError(t('competitions.errorEndBeforeStart'))
      return
    }
    if (draft.starts_at < bound(startsAt) || draft.ends_at > bound(endsAt)) {
      setError(t('competitions.errorRoundOutsideWindow'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const fields = {
        name: draft.name.trim(),
        starts_at: draft.starts_at,
        ends_at: draft.ends_at,
      }
      // Editing keeps the round's place in the list, so sort_order is left
      // alone, and its routes stay assigned because the round itself survives.
      const res = editingId
        ? await fetch(`${apiUrl}/competition-rounds/${editingId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(fields),
          })
        : await fetch(`${apiUrl}/competitions/${competitionId}/rounds`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...fields, sort_order: rounds.length }),
          })
      if (!res.ok) {
        setError(t('competitions.errorSaveFailed'))
        return
      }
      resetForm()
      onRefresh()
    } finally {
      setSaving(false)
    }
  }

  async function removeRound(id: string) {
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/competition-rounds/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (id === editingId) resetForm()
    onRefresh()
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-foreground/60">
        {t('competitions.roundsHint')}{' '}
        {t('competitions.roundsWindowHint', {
          from: stamp(startsAt),
          to: stamp(endsAt),
        })}
      </p>

      {rounds.length === 0 ? (
        <Card className="shadow-sm">
          <CardBody className="py-6 text-center text-foreground/60">
            {/* This is a real configuration, not an empty state to fix. */}
            {t('competitions.roundsEmptyMeansWholeRun')}
          </CardBody>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {rounds.map((round) => {
            const state = competitionState(round, localNow)
            return (
              <li
                key={round.id}
                className={`flex items-center justify-between gap-4 rounded-lg border p-3 ${
                  round.id === editingId ? 'border-primary' : 'border-default-200'
                }`}
              >
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {round.name}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[state]}`}
                    >
                      {t(STATE_KEYS[state])}
                    </span>
                  </p>
                  <p className="text-sm text-foreground/60">
                    {stamp(round.starts_at)} – {stamp(round.ends_at)}
                  </p>
                </div>
                {!locked && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="light" onPress={() => startEditing(round)}>
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      color="danger"
                      onPress={() => void removeRound(round.id)}
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {locked ? (
        <p className="rounded-lg bg-content2 p-3 text-sm text-foreground/60">
          {t('competitions.endedLockNote')}
        </p>
      ) : (
        <Card className="shadow-sm">
          <CardBody className="gap-3">
            <h3 className="font-semibold">
              {t(editingId ? 'competitions.editRound' : 'competitions.addRound')}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('competitions.fieldRoundName')}
                placeholder={t('competitions.roundNamePlaceholder')}
                value={draft.name}
                onValueChange={(v) => setDraft((d) => ({ ...d, name: v }))}
              />
              <div className="hidden sm:block" />
              <DateField
                withTime
                label={t('competitions.fieldStartsAt')}
                min={bound(startsAt)}
                max={bound(endsAt)}
                value={draft.starts_at}
                onValueChange={(v) => setDraft((d) => ({ ...d, starts_at: v }))}
              />
              <DateField
                withTime
                label={t('competitions.fieldEndsAt')}
                min={bound(startsAt)}
                max={bound(endsAt)}
                value={draft.ends_at}
                onValueChange={(v) => setDraft((d) => ({ ...d, ends_at: v }))}
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2">
              <Button color="primary" size="sm" isLoading={saving} onPress={() => void saveRound()}>
                {t(editingId ? 'common.save' : 'competitions.addRound')}
              </Button>
              {editingId && (
                <Button size="sm" variant="light" onPress={resetForm}>
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
